import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@store/authStore';
import httpClient from '@utils/httpClient';

interface PermissionContextType {
    hasAssignedTasks: boolean;
    isLoading: boolean;
    refreshPermissions: () => Promise<void>;
    canEditTask: (task: any, programCreatorId: string) => boolean;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [hasAssignedTasks, setHasAssignedTasks] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const refreshPermissions = async () => {
        if (!user || user.role !== 'Employee') {
            setHasAssignedTasks(false);
            setIsLoading(false);
            return;
        }

        try {
            const response = await httpClient.get('/programs/user/has-assigned-tasks');
            setHasAssignedTasks(response.data.hasAssignedTasks);
        } catch (error) {
            console.error('Error checking assigned tasks:', error);
            setHasAssignedTasks(false);
        } finally {
            setIsLoading(false);
        }
    };

    const canEditTask = (task: any, programCreatorId: string): boolean => {
        if (!user) return false;

        // Admin can edit anything
        if (user.role === 'Admin') return true;

        // Program Manager can edit if they created the program
        if (user.role === 'Program Manager') {
            return programCreatorId === user.id;
        }

        // Employee can edit only their assigned tasks
        if (user.role === 'Employee') {
            return task.assigned_to === user.name || task.assigned_to === user.email;
        }

        return false;
    };

    useEffect(() => {
        refreshPermissions();
    }, [user]);

    return (
        <PermissionContext.Provider value={{ hasAssignedTasks, isLoading, refreshPermissions, canEditTask }}>
            {children}
        </PermissionContext.Provider>
    );
};

export const usePermissions = () => {
    const context = useContext(PermissionContext);
    if (!context) {
        throw new Error('usePermissions must be used within PermissionProvider');
    }
    return context;
};
